import React, { useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { RectangularTextButton } from "components/Base/Buttons";

interface MoveButtonProps {
  direction: "forward" | "backward";
  label: string;
  functionalities: any[];
  selectedID: string;
  onSelectChange: (value: string) => void;
  onMoveClick: () => void;
  inputTypeField?: string;
  outputTypeField?: string;
  inputLabelField?: string;
  outputLabelField?: string;
  disabled?: boolean;
}

const MoveButton: React.FC<MoveButtonProps> = ({
  direction,
  label,
  functionalities,
  selectedID,
  onSelectChange,
  onMoveClick,
  inputTypeField = "inputEdgeType",
  outputTypeField = "outputEdgeType",
  inputLabelField = "inputLabel",
  outputLabelField = "outputLabel",
  disabled = false,
}) => {
  const theme = useTheme();
  const isForward = direction === "forward";

  const buttonText = isForward ? "Move Forward >" : "< Move Back";
  const buttonColor =
    theme.custom.modules.campaigns.primary ?? theme.custom.base.primary.dark;

  const typeField = isForward ? outputTypeField : inputTypeField;
  const labelField = isForward ? outputLabelField : inputLabelField;

  const filtered = functionalities.filter((f) => f[typeField] !== undefined);

  useEffect(() => {
    if (filtered.length > 0 && !selectedID) {
      onSelectChange((isForward ? "output-" : "input-") + filtered[0].id.toString());
    }
  }, [filtered, selectedID, isForward, onSelectChange]);

  const SingleOptionDisplay =
    filtered.length === 1 ? (
      <Box sx={{ minWidth: 180 }}>
        <Typography variant="body2" sx={{ fontWeight: 150 }}>
          {label}
        </Typography>
        <Typography variant="body1">{filtered[0][labelField]}</Typography>
      </Box>
    ) : null;

  const SelectElement =
    filtered.length > 1 ? (
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={selectedID}
          label={label}
          onChange={(e) => onSelectChange(e.target.value)}
        >
          {filtered.map((f) => (
            <MenuItem
              key={f.id}
              value={(isForward ? "output-" : "input-") + f.id.toString()}
            >
              {f[labelField]}
            </MenuItem>
          ))}

          {filtered.length === 0 && (
            <MenuItem disabled>(No matching items)</MenuItem>
          )}
        </Select>
      </FormControl>
    ) : null;

  const ButtonElement = (
    <RectangularTextButton
      colour="clear"
      onClick={onMoveClick}
      disabled={filtered.length === 0 || !selectedID || disabled}
      sx={{ whiteSpace: "nowrap" }}
    >
      {buttonText}
    </RectangularTextButton>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      {isForward ? (
        <>
          {filtered.length === 1 ? SingleOptionDisplay : SelectElement}
          {ButtonElement}
        </>
      ) : (
        <>
          {ButtonElement}
          {filtered.length === 1 ? SingleOptionDisplay : SelectElement}
        </>
      )}
    </Box>
  );
};

export default MoveButton;